import type { HTMLAttributes } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { Card } from '$components/basic/Card';
import { InjectComponent } from '$util/InjectComponent';
import { useOnPropChanged } from '$util/hooks/useOnPropChanged';
import { Styled, transitionSpeed } from './styled';

export enum CardStatus {
  INACTIVE = 'inactive',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  SKIP = 'skip',
}

export enum CardTransitionInjectName {
  HEADER = 'header',
  FOOTER = 'footer',
}

export interface CardTransitionProps extends HTMLAttributes<HTMLDivElement> {
  readonly status: CardStatus;
}

interface CardTransitionState {
  readonly inTransition: boolean;
  readonly height: number | 'auto';
  readonly content: JSX.Element;
}

/**
 * NOTE
 * Rendering of the content happens only when `status` prop changes, this causes side-effects when trying to
 * render things inside this component based on other props.
 * To avoid this, move that state management/component one level up or down.
 */
export function CardTransition({ status, className, children, ...props }: CardTransitionProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>();

  const calculateContentHeight = useCallback(() => {
    const wrapperElement = wrapperRef.current;
    if (!wrapperElement || !wrapperElement.parentElement) return 'auto';
    const padding =
      parseInt(window.getComputedStyle(wrapperElement.parentElement).getPropertyValue('padding'), 10) || 20;
    const contentHeight = wrapperElement.offsetHeight + 2 * padding;
    return contentHeight;
  }, []);

  const renderContent = useCallback(
    () => (
      <>
        <InjectComponent name={CardTransitionInjectName.HEADER} namespace={CardTransition.displayName} />
        <div className="content">{children}</div>
        <InjectComponent name={CardTransitionInjectName.FOOTER} namespace={CardTransition.displayName} />
      </>
    ),
    [children]
  );

  const [{ inTransition, height, content }, setState] = useState<CardTransitionState>({
    inTransition: false,
    height: 'auto',
    content: renderContent(),
  });

  /**
   * @todo height animation doesn't work as expected with 'auto'
   * and if we set the height everytime it doesn't play well with
   * scenarios where the content inside the card has to expand.
   */
  const animateHeight = useCallback(() => {
    const contentHeight = calculateContentHeight();
    if (contentHeight && contentHeight !== height) setState((prev) => ({ ...prev, height: contentHeight }));
  }, [calculateContentHeight, height]);

  const animateContent = () =>
    setState((prev) => ({
      ...prev,
      content: renderContent(),
      height: 'auto',
    }));

  /**
   * Trigger transition when `status` changes.
   */
  useOnPropChanged<CardStatus>(status, () => {
    setState((prev) => ({
      ...prev,
      inTransition: !prev.inTransition,
    }));
  });

  return (
    <Styled className={classNames('CardTransition', `-${status}`, className)} {...props}>
      <CSSTransition
        in={inTransition}
        classNames="card"
        timeout={transitionSpeed}
        onEnter={animateHeight}
        onExit={animateHeight}
        onEntered={animateContent}
        onExited={animateContent}
      >
        <Card style={{ height }}>
          <div
            ref={(el) => {
              wrapperRef.current = el;
              if (el && status === CardStatus.ACTIVE) el.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {content}
          </div>
        </Card>
      </CSSTransition>
    </Styled>
  );
}
CardTransition.displayName = 'CardTransition';
